import { MediaAsset } from "@/types/media";
import { MediaPickerButton } from "../media/MediaPickerButton";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { useEffect, useState } from "react";
import { fetchKQRUrl, updateKHQRUrl } from "@/api/setting-api";
import { toast } from "@/hooks/use-toast";
import { Button } from "../ui/button";
import { Save, RefreshCw, Image as ImageIcon, CheckCircle2 } from "lucide-react";

export function BankAccountQR() {
    const [qrUrl, setQrUrl] = useState<string>("");
    const [originalUrl, setOriginalUrl] = useState<string>("");
    const [isUpdating, setIsUpdating] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const getQRUrl = async () => {
        setIsLoading(true);
        const resp = await fetchKQRUrl();
        if (resp.success) {
            setQrUrl(resp.data || "");
            setOriginalUrl(resp.data || "");
        }
        setIsLoading(false);
    }

    useEffect(() => {
        getQRUrl();
    }, []);

    const handleSave = async () => {
        if (!qrUrl) return;
        setIsUpdating(true);
        const resp = await updateKHQRUrl(qrUrl);
        if (resp.success) {
            setOriginalUrl(qrUrl);
            toast({ title: "Update Successful", description: "Your KHQR has been saved." });
        } else {
            toast({ title: "Error", description: "Failed to update KHQR", variant: "destructive" });
        }
        setIsUpdating(false);
    }

    const hasChanged = qrUrl !== originalUrl;

    return (
        <Card className="overflow-hidden border-slate-200 shadow-sm">
            <CardHeader className="border-b bg-slate-50/50 pb-4">
                <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-3">

                        <img
                            src="/khqr.png"
                            alt="KHQR"
                            className="h-6 w-auto object-contain brightness-110"
                        />
                        <span className="text-lg">Bank KHQR Code</span>
                    </CardTitle>
                    {originalUrl && !hasChanged && (
                        <div className="flex items-center gap-1 text-green-600 text-xs font-medium bg-green-50 px-2 py-1 rounded-full">
                            <CheckCircle2 className="w-3 h-3" />
                            Synced
                        </div>
                    )}
                </div>
                <CardDescription>Customers will scan this QR to pay via Bakong/KHQR.</CardDescription>
            </CardHeader>

            <CardContent className="pt-6">
                <div className="flex flex-col items-center gap-6">
                    {/* QR Display Area */}
                    <div className="relative group">
                        <div className={`w-64 h-64 rounded-2xl border-2 border-dashed flex items-center justify-center overflow-hidden transition-all duration-300 ${hasChanged ? 'border-blue-400 bg-blue-50/30' : 'border-slate-200 bg-white'}`}>
                            {isLoading ? (
                                <RefreshCw className="w-8 h-8 text-slate-300 animate-spin" />
                            ) : qrUrl ? (
                                <img
                                    src={qrUrl}
                                    alt="QR Code"
                                    className="w-full h-full object-contain p-2"
                                />
                            ) : (
                                <div className="text-center px-4">
                                    <ImageIcon className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                                    <p className="text-sm text-slate-500 font-medium">No QR Found</p>
                                    <p className="text-xs text-slate-400">Select an image to start</p>
                                </div>
                            )}
                        </div>

                        {/* Floating Media Picker */}
                        <div className="absolute -bottom-3 -right-3 shadow-lg rounded-full">
                            <MediaPickerButton
                                label={qrUrl ? "Change" : "Upload QR"}
                                onPick={(items: MediaAsset[]) => setQrUrl(items[0]?.s3Url || items[0]?.fileUrl || "")}
                            />
                        </div>
                    </div>

                    {/* Action Footer */}
                    <div className="w-full pt-4 flex gap-3">
                        {hasChanged && (
                            <Button
                                variant="outline"
                                className="flex-1"
                                onClick={() => setQrUrl(originalUrl)}
                                disabled={isUpdating}
                            >
                                Cancel
                            </Button>
                        )}
                        <Button
                            className="flex-1 shadow-md transition-all active:scale-95"
                            disabled={!hasChanged || isUpdating}
                            onClick={handleSave}
                        >
                            {isUpdating ? (
                                <><RefreshCw className="mr-2 h-4 w-4 animate-spin" /> Saving...</>
                            ) : (
                                <><Save className="mr-2 h-4 w-4" /> Save KHQR</>
                            )}
                        </Button>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}